import { Controller } from "@hotwired/stimulus"
import { Toast } from "bootstrap"

export default class extends Controller {
  static targets = [
    "searchInput",
    "productGrid",
    "categoryBtn",
    "cartItems",
    "cartEmpty",
    "cartBadge",
    "subtotal",
    "discountRow",
    "discountAmount",
    "discountInput",
    "taxAmount",
    "totalAmount",
    "checkoutBtn",
    "customerSearch",
    "customerDropdown",
    "customerSelected",
    "customerName",
    "paymentModal",
    "paymentMethodBtns",
    "cashSection",
    "cardSection",
    "cashInput",
    "changeAmount",
    "payBtn",
    "modalTotal",
    "modalItemCount",
    "barcodeInput",
    "categoryContainer",
    "quickCash",
    "loyaltyPanel",
    "loyaltyInfo",
    "redeemToggle",
    "redeemRow",
    "redeemAmount",
    "pointsEarnedRow",
    "pointsEarnedAmount",
    "receiptFrame",
    "moneySection",
    "creditSection",
    "creditInfo",
    "splitSection",
    "splitMethod1",
    "splitMethod2",
    "splitAmount1",
    "splitAmount2",
    "splitRemaining",
    "splitBar",
    "splitMessage",
    "promoRow",
    "promoAmount",
    "promoLabel",
    "cartSearchDropdown",
    "panelToggleIcon",
    "newCustomerModal",
    "newCustomerName",
    "newCustomerPhone",
    "newCustomerEmail",
    "newCustomerError",
    "newCustomerSaveBtn",
    "helpOverlay",
    "resizeHandle",
    "panelToggle"
  ]

  static values = {
    products: Array,
    categories: Array,
    customers: Array,
    createUrl: String,
    previewUrl: String,
    csrfToken: String,
    currencySymbol: { type: String, default: "$" },
    denominations: { type: Array, default: [5, 10, 20, 50, 100] },
    pointsPerUnit: { type: Number, default: 1 },
    taxInclusive: { type: Boolean, default: false },
    autoPrint: { type: Boolean, default: true },
    quickCreateCustomerUrl: String
  }

  get sym() { return this.currencySymbolValue }

  connect() {
    this.cart = []
    this.selectedCategoryId = null
    this.selectedCustomerId = null
    this.selectedCustomerData = null
    this.paymentMethod = "cash"
    this.discountPct = 0
    this.redeemingPoints = false
    this._promoDiscount = 0
    this._promoName = null
    this._promoId = null
    this._promoTimer = null

    // Roving cursors, kept here so they survive a re-render.
    this._gridIndex = 0
    this._cartIndex = 0
    this._searchIndex = -1
    this._customerIndex = -1
    this._helpOpen = false
    this._focusStack = []
    this._pendingCartFocus = null

    this._leftColEl = this.element.querySelector('.pos-left-col')
    this._leftPanelHidden = false
    this._resizing = false
    this._onResize = this._onResize.bind(this)
    this._stopResize = this._stopResize.bind(this)
    this._onDocClick = this._onDocClick.bind(this)

    this.renderCategories()
    this.renderProducts()
    this.updateCart()
    this.renderHeldSales()

    if (this.hasBarcodeInputTarget) this.barcodeInputTarget.focus()

    this.handleKeydown = this.handleKeydown.bind(this)
    document.addEventListener("keydown", this.handleKeydown)
    document.addEventListener("click", this._onDocClick)
  }

  disconnect() {
    document.removeEventListener("keydown", this.handleKeydown)
    document.removeEventListener("mousemove", this._onResize)
    document.removeEventListener("mouseup", this._stopResize)
    document.removeEventListener("click", this._onDocClick)
  }

  // Keyboard
  // A scanner types plain characters, so global bindings are function keys and
  // Alt combos only; bare keys act only inside a focused region.

  handleKeydown(event) {
    // Help is modal over everything, including the payment sheet.
    if (this._helpOpen) {
      if (event.key === "Escape" || event.key === "F1") {
        event.preventDefault()
        this.closeHelp()
      } else if (event.key === "Tab") {
        this._trapFocus(this.helpOverlayTarget, event)
      }
      return
    }

    if (event.key === "F1") { event.preventDefault(); this.openHelp(); return }

    // The frontmost sheet owns the keyboard while it is up.
    if (this._isOpen(this.hasNewCustomerModalTarget && this.newCustomerModalTarget)) {
      return this._newCustomerKeys(event)
    }
    if (this._isOpen(this.hasPaymentModalTarget && this.paymentModalTarget)) {
      return this._paymentKeys(event)
    }

    switch (event.key) {
      case "F2": event.preventDefault(); this.focusSearch(); return
      case "F3": event.preventDefault(); this.focusCustomer(); return
      case "F4": event.preventDefault(); this.focusBarcode(); return
      case "F6": event.preventDefault(); this.focusGrid(); return
      case "F7": event.preventDefault(); this.focusCart(); return
      case "F8": event.preventDefault(); this.printReceipt(); return
      case "F8": event.preventDefault(); this.holdSale(); return
      case "F9": event.preventDefault(); this.openPayment(); return
    }

    if (event.altKey && !event.ctrlKey && !event.metaKey && this._altShortcut(event)) return

    if (event.key === "Escape" && this._isTextEntry(document.activeElement)) {
      this._closeDropdowns()
      this.focusBarcode()
    }
  }

  // event.code, so the bindings hold on non-QWERTY layouts.
  _altShortcut(event) {
    switch (event.code) {
      case "KeyD":
        if (!this.hasDiscountInputTarget) return false
        event.preventDefault()
        this.discountInputTarget.focus()
        this.discountInputTarget.select()
        return true
      case "KeyH": event.preventDefault(); this.focusHeldSales(); return true
      case "KeyC": event.preventDefault(); this.clearCart(); return true
      case "KeyP": event.preventDefault(); this.toggleLeftPanel(); return true
      case "KeyN": event.preventDefault(); this.openNewCustomer(); return true
      case "KeyR":
        event.preventDefault()
        // Silent no-op when there is nothing to redeem, matching the toggle.
        this.toggleRedeemPoints()
        return true
      default: return false
    }
  }

  // Focus movers

  focusSearch() {
    if (!this.hasSearchInputTarget) return
    this.searchInputTarget.focus()
    this.searchInputTarget.select()
  }

  focusBarcode() {
    if (!this.hasBarcodeInputTarget) return
    this.barcodeInputTarget.focus()
    this.barcodeInputTarget.select()
  }

  focusCustomer() {
    if (!this.hasCustomerSearchTarget) return
    this.customerSearchTarget.focus()
    this.customerSearchTarget.select()
  }

  focusGrid() {
    const cards = this._gridCards()
    if (!cards.length) { this.focusSearch(); return }
    // Focus sent into a display:none subtree is silently dropped.
    if (this._leftPanelHidden) this.toggleLeftPanel()
    this._focusGridCard(Math.min(this._gridIndex, cards.length - 1))
  }

  focusCart() {
    const rows = this._cartRows()
    if (!rows.length) { this.showToast("Cart is empty", "info"); return }
    this._focusCartRow(Math.min(this._cartIndex, rows.length - 1))
  }

  focusHeldSales() {
    const card = this.element.querySelector(".held-card")
    if (!card) { this.showToast("No held sales", "info"); return }
    // The sidebar is d-none below lg.
    if (card.offsetParent === null) {
      this.showToast("Held sales are not shown at this screen size", "info")
      return
    }
    card.focus()
  }

  // Shared helpers

  _isTextEntry(el) {
    if (!el) return false
    const tag = (el.tagName || "").toLowerCase()
    return tag === "input" || tag === "textarea" || tag === "select" || el.isContentEditable
  }

  _isOpen(el) {
    return !!el && el.classList.contains("show")
  }

  _focusables(root) {
    const sel = 'a[href], button:not([disabled]), input:not([disabled]), ' +
      'select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    // offsetParent screens out the hidden payment method sections.
    return Array.from(root.querySelectorAll(sel)).filter(el => el.offsetParent !== null)
  }

  // These modals are toggled by hand, so nothing else holds focus in.
  _trapFocus(container, event) {
    if (event.key !== "Tab") return
    const items = this._focusables(container)
    if (!items.length) return
    const first = items[0]
    const last = items[items.length - 1]
    const on = document.activeElement

    if (event.shiftKey && (on === first || !container.contains(on))) {
      event.preventDefault(); last.focus()
    } else if (!event.shiftKey && (on === last || !container.contains(on))) {
      event.preventDefault(); first.focus()
    }
  }

  // A stack, not one slot: the sheets nest (F1 over an open payment sheet).
  _rememberFocus() {
    this._focusStack.push(document.activeElement)
  }

  _restoreFocus(fallback) {
    const el = this._focusStack.pop()
    // The remembered control can be gone — a sale rebuilds the cart.
    if (el && el.isConnected && el.offsetParent !== null && !el.disabled) {
      el.focus()
      return
    }
    if (fallback) fallback()
  }

  _closeDropdowns() {
    // Options are real buttons, so hide with focus on one hands it to <body>.
    const rescue = (box, input) => {
      if (box.contains(document.activeElement) && input) input.focus()
    }

    if (this.hasCartSearchDropdownTarget) {
      rescue(this.cartSearchDropdownTarget, this.hasSearchInputTarget ? this.searchInputTarget : null)
      this.cartSearchDropdownTarget.classList.add("d-none")
      this._searchIndex = -1
      if (this.hasSearchInputTarget) {
        this.searchInputTarget.setAttribute("aria-expanded", "false")
        this._setActiveDescendant(this.searchInputTarget, null)
      }
    }
    if (this.hasCustomerDropdownTarget) {
      rescue(this.customerDropdownTarget, this.hasCustomerSearchTarget ? this.customerSearchTarget : null)
      this.customerDropdownTarget.classList.add("d-none")
      this._customerIndex = -1
      if (this.hasCustomerSearchTarget) {
        this.customerSearchTarget.setAttribute("aria-expanded", "false")
        this._setActiveDescendant(this.customerSearchTarget, null)
      }
    }
  }

  // Help overlay

  toggleHelp() {
    this._helpOpen ? this.closeHelp() : this.openHelp()
  }

  openHelp() {
    if (!this.hasHelpOverlayTarget) return
    this._rememberFocus()
    this._helpOpen = true
    this.helpOverlayTarget.hidden = false
    const close = this.helpOverlayTarget.querySelector(".btn-close")
    if (close) close.focus()
  }

  closeHelp() {
    if (!this.hasHelpOverlayTarget) return
    this._helpOpen = false
    this.helpOverlayTarget.hidden = true
    this._restoreFocus(() => this.focusBarcode())
  }

  // Product grid navigation

  _gridCards() {
    return this.hasProductGridTarget
      ? Array.from(this.productGridTarget.querySelectorAll(".product-card"))
      : []
  }

  // Read off the layout, not the breakpoint: the panel is resizable.
  _gridColumns(cards) {
    if (cards.length < 2) return 1
    const top = cards[0].getBoundingClientRect().top
    let n = 0
    for (const c of cards) {
      if (Math.abs(c.getBoundingClientRect().top - top) > 1) break
      n++
    }
    return Math.max(1, n)
  }

  _focusGridCard(index) {
    const cards = this._gridCards()
    // A filter that empties the grid would drop focus on a removed node.
    if (!cards.length) { this.focusSearch(); return }
    const i = Math.max(0, Math.min(index, cards.length - 1))
    this._gridIndex = i
    cards.forEach((c, n) => {
      c.tabIndex = n === i ? 0 : -1
      c.classList.toggle("is-kb-cursor", n === i)
    })
    cards[i].focus({ preventScroll: true })
    cards[i].scrollIntoView({ block: "nearest" })
  }

  gridKeys(event) {
    const card = event.target.closest(".product-card")
    if (!card) return

    const cards = this._gridCards()
    const cols = this._gridColumns(cards)
    let i = cards.indexOf(card)
    if (i < 0) return

    switch (event.key) {
      case "ArrowRight": i = Math.min(i + 1, cards.length - 1); break
      case "ArrowLeft": i = Math.max(i - 1, 0); break
      case "ArrowDown": i = Math.min(i + cols, cards.length - 1); break
      case "ArrowUp":
        if (i - cols < 0) { event.preventDefault(); this.focusSearch(); return }
        i -= cols
        break
      case "PageDown": i = Math.min(i + cols * 3, cards.length - 1); break
      case "PageUp": i = Math.max(i - cols * 3, 0); break
      case "Home": i = 0; break
      case "End": i = cards.length - 1; break
      case "Enter":
      case " ": {
        event.preventDefault()
        const id = parseInt(card.dataset.productId)
        const product = this.productsValue.find(p => p.id === id)
        // Not a synthetic click — out-of-stock cards carry no click action.
        if (product) this.addProductToCart(product)
        return
      }
      case "[": event.preventDefault(); this._stepCategory(-1); return
      case "]": event.preventDefault(); this._stepCategory(1); return
      default: return
    }

    event.preventDefault()
    this._focusGridCard(i)
  }

  _stepCategory(delta) {
    const pills = this.hasCategoryBtnTarget ? this.categoryBtnTargets : []
    if (!pills.length) return
    const current = pills.findIndex(p => p.classList.contains("active"))
    const next = Math.max(0, Math.min((current < 0 ? 0 : current) + delta, pills.length - 1))
    pills[next].click()
    // The grid is rebuilt under the cursor, so re-seat it at the top.
    this._gridIndex = 0
    this._focusGridCard(0)
  }

  // Cart line navigation

  _cartRows() {
    return this.hasCartItemsTarget
      ? Array.from(this.cartItemsTarget.querySelectorAll(".cart-item"))
      : []
  }

  _focusCartRow(index) {
    const rows = this._cartRows()
    if (!rows.length) return
    const i = Math.max(0, Math.min(index, rows.length - 1))
    this._cartIndex = i
    rows.forEach((r, n) => {
      r.tabIndex = n === i ? 0 : -1
      r.classList.toggle("is-kb-cursor", n === i)
    })
    rows[i].focus({ preventScroll: true })
    rows[i].scrollIntoView({ block: "nearest" })
  }

  cartKeys(event) {
    const row = event.target.closest(".cart-item")
    if (!row) return

    // Inside a field the plain keys belong to the field.
    if (this._isTextEntry(event.target)) {
      if (event.key === "Escape" || event.key === "Enter") {
        event.preventDefault()
        event.target.blur()
        this._focusCartRow(this._cartRows().indexOf(row))
      }
      return
    }

    const rows = this._cartRows()
    const i = rows.indexOf(row)
    if (i < 0) return
    // The row carries the same productId its buttons do, so it stands in.
    const fire = (method) => {
      this._pendingCartFocus = i
      this[method]({ currentTarget: row })
      // A pending one would later yank focus into the cart.
      this._pendingCartFocus = null
    }

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        if (i === rows.length - 1) return
        this._focusCartRow(i + 1)
        return
      case "ArrowUp":
        event.preventDefault()
        if (i === 0) { this.focusSearch(); return }
        this._focusCartRow(i - 1)
        return
      case "Home": event.preventDefault(); this._focusCartRow(0); return
      case "End": event.preventDefault(); this._focusCartRow(rows.length - 1); return

      case "+":
      case "=":
      case "ArrowRight":
        event.preventDefault()
        fire("incrementQty")
        return

      case "-":
      case "_":
      case "ArrowLeft":
        event.preventDefault()
        fire("decrementQty")
        return

      case "Delete":
      case "Backspace":
        event.preventDefault()
        fire("removeItem")
        return

      case "Enter": {
        event.preventDefault()
        const qty = row.querySelector(".qty-input")
        if (qty) { qty.focus(); qty.select() }
        return
      }

      case "d":
      case "D": {
        event.preventDefault()
        const disc = row.querySelector("[data-action*='updateItemDiscount']")
        if (disc) { disc.focus(); disc.select() }
        return
      }
    }

    // Safe as a bare key: a scanner burst never lands on a cart row.
    if (/^[0-9]$/.test(event.key)) {
      event.preventDefault()
      const qty = row.querySelector(".qty-input")
      if (qty) {
        qty.focus()
        qty.value = event.key
        qty.dispatchEvent(new Event("change", { bubbles: true }))
        qty.select()
      }
      return
    }
  }

  quickCashKeys(event) {
    const btns = Array.from(this.quickCashTarget.querySelectorAll(".denom-btn"))
    const i = btns.indexOf(event.target)
    if (i < 0) return
    if (event.key === "ArrowRight" || event.key === "ArrowDown") {
      event.preventDefault(); btns[Math.min(i + 1, btns.length - 1)].focus()
    } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
      event.preventDefault(); btns[Math.max(i - 1, 0)].focus()
    }
  }

  // Held sales

  heldKeys(event) {
    const card = event.target.closest(".held-card")
    if (!card) return
    const cards = Array.from(this.element.querySelectorAll(".held-card"))
    const i = cards.indexOf(card)

    switch (event.key) {
      case "Enter":
      case " ":
        // The discard button answers Enter itself.
        if (event.target.closest(".held-card-delete")) return
        event.preventDefault()
        this.recallSale(parseInt(card.dataset.heldId))
        return
      case "Delete":
      case "Backspace":
        event.preventDefault()
        this.deleteHeld({ currentTarget: card, stopPropagation() { } })
        return
      case "ArrowDown":
        event.preventDefault(); cards[Math.min(i + 1, cards.length - 1)]?.focus(); return
      case "ArrowUp":
        event.preventDefault(); cards[Math.max(i - 1, 0)]?.focus(); return
    }
  }

  renderCategories() {
    if (!this.hasCategoryContainerTarget) return
    const cats = this.categoriesValue

    let html = `<button class="cat-pill active"
      data-pos-target="categoryBtn"
      data-action="click->pos#filterCategory"
      data-category-id="all">All</button>`

    cats.forEach(c => {
      html += `<button class="cat-pill"
        data-pos-target="categoryBtn"
        data-action="click->pos#filterCategory"
        data-category-id="${c.id}">${this.escapeHtml(c.name)}</button>`
    })

    this.categoryContainerTarget.innerHTML = html
  }

  startResize(e) {
    if (e.target.closest('.pos-panel-toggle')) return
    e.preventDefault()
    this._resizing = true
    this._startX = e.clientX
    this._startW = this._leftColEl.offsetWidth
    document.addEventListener('mousemove', this._onResize)
    document.addEventListener('mouseup', this._stopResize)
  }

  _onResize(e) {
    if (!this._resizing) return
    this._setLeftWidth(this._startW + (e.clientX - this._startX))
  }

  _stopResize() {
    this._resizing = false
    document.removeEventListener('mousemove', this._onResize)
    document.removeEventListener('mouseup', this._stopResize)
  }

  toggleLeftPanel() {
    this._leftPanelHidden = !this._leftPanelHidden
    if (this._leftPanelHidden) {
      this._leftColEl.style.display = 'none'
    } else {
      this._leftColEl.style.display = ''
      if (!this._leftColEl.style.width) {
        this._leftColEl.style.flex = '1'
      }
    }
    if (this.hasPanelToggleIconTarget) {
      this.panelToggleIconTarget.className =
        `bi bi-chevron-${this._leftPanelHidden ? 'right' : 'left'}`
    }
    if (this.hasPanelToggleTarget) {
      this.panelToggleTarget.setAttribute("aria-expanded", String(!this._leftPanelHidden))
    }
    // The column is display:none now, so move focus somewhere visible.
    if (this._leftPanelHidden && document.activeElement?.closest?.(".pos-left-col")) {
      this.focusSearch()
    }
  }

  showSearchDropdown() {
    if (this.hasSearchInputTarget && this.searchInputTarget.value.trim()) {
      this._updateCartSearchDropdown(this.searchInputTarget.value.trim().toLowerCase())
    }
  }

  // Combobox keys
  // Arrows move a highlight while focus stays in the input; select dispatches a
  // real click so the existing click actions still run.

  _dropdownOptions(container) {
    return Array.from(container.querySelectorAll("[data-kb-option]"))
  }

  _moveDropdown(container, indexKey, delta) {
    const options = this._dropdownOptions(container)
    if (!options.length) return
    let i = this[indexKey] + delta
    // Wraps, so holding one arrow always reaches every match.
    if (i < 0) i = options.length - 1
    if (i >= options.length) i = 0
    this[indexKey] = i

    options.forEach((o, n) => {
      const on = n === i
      o.classList.toggle("is-kb-active", on)
      o.setAttribute("aria-selected", on ? "true" : "false")
      if (on) {
        o.id = o.id || `pos-opt-${indexKey}-${n}`
        o.scrollIntoView({ block: "nearest" })
      }
    })
    return options[i]
  }

  searchKeys(event) {
    if (!this.hasCartSearchDropdownTarget) return
    const box = this.cartSearchDropdownTarget
    const open = !box.classList.contains("d-none")
    const options = this._dropdownOptions(box)

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        if (!open || !options.length) { this.focusGrid(); return }
        this._setActiveDescendant(this.searchInputTarget, this._moveDropdown(box, "_searchIndex", 1))
        return
      case "ArrowUp":
        if (!open || !options.length) return
        event.preventDefault()
        this._setActiveDescendant(this.searchInputTarget, this._moveDropdown(box, "_searchIndex", -1))
        return
      case "Enter": {
        event.preventDefault()
        if (!open || !options.length) return
        const pick = options[this._searchIndex] || options[0]
        pick.click()
        return
      }
      case "Escape":
        if (!open) return   // Let it bubble: nothing to dismiss, so Escape means "back to the scanner".
        // Stop here, or the document handler pulls focus out of the field too.
        event.preventDefault()
        event.stopPropagation()
        this._closeDropdowns()
        return
      case "Tab":
        this._closeDropdowns()
        return
    }
  }

  customerKeys(event) {
    if (!this.hasCustomerDropdownTarget) return
    const box = this.customerDropdownTarget
    const open = !box.classList.contains("d-none")
    const options = this._dropdownOptions(box)

    switch (event.key) {
      case "ArrowDown":
        if (!open || !options.length) return
        event.preventDefault()
        this._setActiveDescendant(this.customerSearchTarget, this._moveDropdown(box, "_customerIndex", 1))
        return
      case "ArrowUp":
        if (!open || !options.length) return
        event.preventDefault()
        this._setActiveDescendant(this.customerSearchTarget, this._moveDropdown(box, "_customerIndex", -1))
        return
      case "Enter": {
        event.preventDefault()
        if (!open || !options.length) return
        const pick = options[this._customerIndex] || options[0]
        pick.click()
        return
      }
      case "Escape":
        if (!open) return   // Nothing to dismiss — let Escape fall through to the scanner.
        event.preventDefault()
        event.stopPropagation()
        this._closeDropdowns()
        return
      case "Tab":
        this._closeDropdowns()
        return
    }
  }

  _setActiveDescendant(input, option) {
    if (!input) return
    if (option && option.id) input.setAttribute("aria-activedescendant", option.id)
    else input.removeAttribute("aria-activedescendant")
  }

  _updateCartSearchDropdown(query) {
    if (!this.hasCartSearchDropdownTarget) return
    const dropdown = this.cartSearchDropdownTarget
    // Clear the highlight too, or Enter fires at a stale match.
    const hide = () => {
      dropdown.classList.add('d-none')
      this._searchIndex = -1
      this.searchInputTarget.setAttribute("aria-expanded", "false")
      this._setActiveDescendant(this.searchInputTarget, null)
    }

    if (!query) { dropdown.innerHTML = ''; hide(); return }

    const results = this.productsValue
      .filter(p => p.name.toLowerCase().includes(query) ||
        (p.sku && p.sku.toLowerCase().includes(query)) ||
        (p.barcode && p.barcode.toLowerCase().includes(query)))
      .slice(0, 9)

    if (!results.length) { hide(); return }

    dropdown.innerHTML = results.map((p, n) => {
      const stock = p.stock <= 0
        ? `<span style="color:#dc2626;font-size:.65rem;">Out</span>`
        : p.stock <= 5
          ? `<span style="color:#d97706;font-size:.65rem;">${p.stock} left</span>`
          : ''
      return `<div class="list-group-item list-group-item-action d-flex align-items-center gap-2"
                   role="option" aria-selected="false" data-kb-option
                   id="pos-search-opt-${n}"
                   data-action="click->pos#addFromDropdown"
                   data-product-id="${p.id}">
        <div class="flex-grow-1 min-width-0">
          <div class="fw-medium" style="white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${this.escapeHtml(p.name)}</div>
          ${p.sku ? `<div style="font-size:.63rem;color:#6b7280;">${this.escapeHtml(p.sku)}</div>` : ''}
        </div>
        ${stock}
        <span style="color:#248317;font-weight:700;flex-shrink:0;">${this.sym}${p.price.toFixed(2)}</span>
      </div>`
    }).join('')

    // Rebuilt every keystroke, so the highlight starts over.
    this._searchIndex = -1
    this._setActiveDescendant(this.searchInputTarget, null)
    dropdown.classList.remove('d-none')
    this.searchInputTarget.setAttribute("aria-expanded", "true")
  }

  addFromDropdown(e) {
    const id = parseInt(e.currentTarget.dataset.productId)
    const product = this.productsValue.find(p => p.id === id)
    if (product) {
      this.addProductToCart(product)
      this.searchInputTarget.value = ''
      this.cartSearchDropdownTarget.innerHTML = ''
      this._closeDropdowns()
      this.renderProducts()
    }
  }

  _onDocClick(e) {
    if (!this.hasCartSearchDropdownTarget) return
    if (!this.cartSearchDropdownTarget.contains(e.target) &&
      !this.searchInputTarget.contains(e.target)) {
      this.cartSearchDropdownTarget.classList.add('d-none')
    }
  }

  filterCategory(event) {
    const id = event.currentTarget.dataset.categoryId
    this.selectedCategoryId = id === "all" ? null : parseInt(id)

    this.categoryBtnTargets.forEach(btn => btn.classList.remove("active"))
    event.currentTarget.classList.add("active")

    this.renderProducts()
  }

  search() {
    this.renderProducts()
    const q = this.searchInputTarget.value.trim().toLowerCase()
    this._updateCartSearchDropdown(q)
  }

  scanBarcode(event) {
    if (event.key !== "Enter") return
    event.preventDefault()
    const barcode = this.barcodeInputTarget.value.trim()
    if (!barcode) return

    const product = this.productsValue.find(p => p.barcode === barcode || p.sku === barcode)
    if (product) {
      this.addProductToCart(product)
      this.barcodeInputTarget.value = ""
      this.showToast(`${product.name} scanned`, "success")
    } else {
      this.showToast("Product not found", "danger")
      this.barcodeInputTarget.value = ""
    }
  }

  renderProducts() {
    const query = this.searchInputTarget.value.toLowerCase().trim()
    let filtered = this.productsValue

    if (this.selectedCategoryId) {
      filtered = filtered.filter(p => p.category_id === this.selectedCategoryId)
    }
    if (query) {
      filtered = filtered.filter(p =>
        p.name.toLowerCase().includes(query) ||
        (p.sku && p.sku.toLowerCase().includes(query)) ||
        (p.barcode && p.barcode.toLowerCase().includes(query))
      )
    }

    if (filtered.length === 0) {
      this.productGridTarget.innerHTML = `
        <div class="pos-grid-empty text-center py-5">
          <div style="width:56px;height:56px;border-radius:50%;background:#f3f4f6;display:flex;align-items:center;justify-content:center;margin:0 auto .75rem;">
            <i class="bi bi-search" style="font-size:1.4rem;"></i>
          </div>
          <p class="mb-0" style="font-size:.85rem;font-weight:500;">No products found</p>
          <small style="font-size:.75rem;">Try a different search or category</small>
        </div>`
      this._gridIndex = 0
      return
    }

    // Only hand focus back if it was in the grid to begin with.
    const hadFocus = document.activeElement?.classList?.contains("product-card")

    this.productGridTarget.innerHTML = filtered.map((p, idx) => {
      const oos = p.stock <= 0 || p.expiry_status === "expired"
      const lowStock = p.stock > 0 && p.stock <= 5
      const expiring = p.expiry_status === "expiring"

      const stockBadge = oos
        ? `<span class="stock-out"><i class="bi bi-x-circle me-1"></i>${p.expiry_status === "expired" ? "Expired" : "Out of stock"}</span>`
        : lowStock
          ? `<span class="stock-low"><i class="bi bi-exclamation-circle me-1"></i>${p.stock} left</span>`
          : `<span class="stock-ok"><i class="bi bi-check-circle me-1"></i>${p.stock}</span>`

      const expiryBadge = expiring
        ? `<div style="position:absolute;top:4px;right:4px;background:#f59e0b;color:#fff;font-size:.55rem;font-weight:700;padding:1px 5px;border-radius:4px;line-height:1.4;"><i class="bi bi-clock me-1"></i>EXP ${p.nearest_expiry}</div>`
        : ""

      const skuHtml = p.sku
        ? `<div class="pc-sku">${this.escapeHtml(p.sku)}</div>`
        : ""

      // The picture layers over an always-present placeholder icon, so a failed
      // image degrades with no error handler and the grid never reflows.
      const imgHtml = p.image_url
        ? `<img src="${this.escapeHtml(p.image_url)}" alt="" loading="lazy" decoding="async">`
        : ""
      const thumbHtml = `
        <div class="pc-thumb">
          <i class="bi bi-box-seam"></i>
          ${imgHtml}
        </div>`

      // aria-disabled, not untabbable: say it's out, don't skip it.
      const label = [
        p.name,
        `${this.sym}${p.price.toFixed(2)}`,
        p.expiry_status === "expired" ? "expired"
          : p.stock <= 0 ? "out of stock"
            : `${p.stock} in stock`
      ].join(", ")

      return `
        <div class="product-card ${oos ? 'out-of-stock' : ''}"
             ${oos ? '' : `data-action="click->pos#addFromGrid"`}
             data-product-id="${p.id}"
             role="button"
             tabindex="${idx === 0 ? 0 : -1}"
             ${oos ? 'aria-disabled="true"' : ''}
             aria-label="${this.escapeHtml(label)}"
             style="${expiring && !oos ? 'border-color:#fde68a;' : ''}">
          ${expiryBadge}
          <div class="pc-body">
            ${thumbHtml}
            <div class="pc-text">
              ${skuHtml}
              <div class="pc-name">${this.escapeHtml(p.name)}</div>
              <div class="pc-price">${this.sym}${p.price.toFixed(2)}</div>
            </div>
          </div>
          <div class="pc-footer">
            ${stockBadge}
            ${!oos ? `<i class="bi bi-plus-circle" style="color:#d1d5db;font-size:.8rem;"></i>` : ''}
          </div>
        </div>`
    }).join("")

    if (hadFocus) {
      // A narrowed result set can be shorter than where the cursor sat.
      this._focusGridCard(Math.min(this._gridIndex, filtered.length - 1))
    } else {
      this._gridIndex = 0
    }
  }

  addFromGrid(event) {
    const productId = parseInt(event.currentTarget.dataset.productId)
    const product = this.productsValue.find(p => p.id === productId)
    if (product) this.addProductToCart(product)
  }

  addProductToCart(product) {
    if (product.expiry_status === "expired") {
      this.showToast(`${product.name} has expired and cannot be sold`, "danger"); return
    }
    if (product.stock <= 0) {
      this.showToast(`${product.name} is out of stock`, "danger"); return
    }
    if (product.expiry_status === "expiring") {
      this.showToast(`${product.name} expires ${product.nearest_expiry}`, "warning")
    }

    const existing = this.cart.find(i => i.product_id === product.id)
    if (existing) {
      if (existing.quantity >= product.stock) {
        this.showToast(`Only ${product.stock} in stock`, "warning"); return
      }
      existing.quantity += 1
    } else {
      this.cart.push({
        product_id: product.id,
        name: product.name,
        price: product.price,
        cost_price: product.cost_price,
        tax_rate: product.tax_rate,
        quantity: 1,
        stock: product.stock,
        discount_pct: 0
      })
    }

    this.updateCart()
  }

  incrementQty(event) {
    const item = this.cartItemByEvent(event)
    if (!item) return
    if (item.quantity >= item.stock) { this.showToast(`Only ${item.stock} in stock`, "warning"); return }
    item.quantity += 1
    this.updateCart()
  }

  decrementQty(event) {
    const item = this.cartItemByEvent(event)
    if (!item) return
    if (item.quantity <= 1) { this.removeItem(event); return }
    item.quantity -= 1
    this.updateCart()
  }

  updateQtyDirect(event) {
    const item = this.cartItemByEvent(event)
    if (!item) return
    let qty = parseInt(event.currentTarget.value)
    if (isNaN(qty) || qty < 1) qty = 1
    if (qty > item.stock) { qty = item.stock; this.showToast(`Only ${item.stock} in stock`, "warning") }
    item.quantity = qty
    this.updateCart()
  }

  removeItem(event) {
    const productId = parseInt(event.currentTarget.dataset.productId)
    this.cart = this.cart.filter(i => i.product_id !== productId)
    this.updateCart()
  }

  clearCart() {
    if (this.cart.length === 0) return
    if (!confirm("Clear all items from cart?")) return
    this.cart = []
    this.discountPct = 0
    if (this.hasDiscountInputTarget) this.discountInputTarget.value = ""
    this.selectedCustomerId = null
    this.clearCustomer()
    this.updateCart()
  }

  holdSale() {
    if (this.cart.length === 0) { this.showToast("Cart is empty — nothing to hold.", "warning"); return }
    const held = JSON.parse(localStorage.getItem("pos_held_sales") || "[]")
    held.push({
      id: Date.now(),
      cart: this.cart,
      discountPct: this.discountPct,
      customerId: this.selectedCustomerId,
      customerData: this.selectedCustomerData,
      promoId: this._promoId,
      promoName: this._promoName,
      promoDiscount: this._promoDiscount
    })
    localStorage.setItem("pos_held_sales", JSON.stringify(held))
    this.cart = []
    this.discountPct = 0
    this._promoDiscount = 0; this._promoName = null; this._promoId = null
    if (this.hasDiscountInputTarget) this.discountInputTarget.value = ""
    this.selectedCustomerId = null
    this.clearCustomer()
    this.updateCart()
    this.renderHeldSales()
    this.showToast("Sale held. Cart cleared for new sale.", "info")
  }

  recallSale(heldId) {
    if (this.cart.length > 0 && !confirm("Current cart will be replaced. Continue?")) return
    const held = JSON.parse(localStorage.getItem("pos_held_sales") || "[]")
    const idx = held.findIndex(s => s.id === heldId)
    if (idx === -1) return
    const sale = held.splice(idx, 1)[0]
    localStorage.setItem("pos_held_sales", JSON.stringify(held))
    this.cart = sale.cart
    this.discountPct = sale.discountPct || 0
    this._promoId = sale.promoId
    this._promoName = sale.promoName
    this._promoDiscount = sale.promoDiscount || 0
    this.selectedCustomerId = sale.customerId
    this.selectedCustomerData = sale.customerData
    if (this.hasDiscountInputTarget) this.discountInputTarget.value = this.discountPct || ""
    if (sale.customerData) this._applyCustomer(sale.customerData)
    this.updateCart()
    this.renderHeldSales()
  }

  renderHeldSales() {
    const held = JSON.parse(localStorage.getItem("pos_held_sales") || "[]")
    const target = this.element.querySelector("[data-pos-target='heldSalesList']")
    if (!target) return
    const badge = this.element.querySelector("[data-pos-target='heldBadge']")
    if (badge) { badge.textContent = held.length; badge.classList.toggle("d-none", held.length === 0) }
    target.innerHTML = held.length === 0
      ? `<p class="held-empty">No held sales</p>`
      : held.map(s => {
        const units = s.cart.reduce((n, i) => n + i.quantity, 0)
        const total = s.cart.reduce((n, i) => n + i.price * i.quantity, 0)
        const customer = s.customerData ? s.customerData.name : "Walk-in"
        const when = this.timeAgo(s.id)
        // Whole card recalls; the delete button stops propagation.
        return `
        <div class="held-card" role="button" tabindex="0" title="Recall this sale"
             aria-label="Recall held sale for ${this.escapeHtml(customer)}, ${units} item${units === 1 ? "" : "s"}, ${this.sym}${total.toFixed(2)}. Delete key discards it."
             data-action="click->pos#recallHeld keydown->pos#heldKeys"
             data-held-id="${s.id}">
          <div class="held-card-main">
            <div class="held-card-top">
              <span class="held-card-customer">${this.escapeHtml(customer)}</span>
              <span class="held-card-total">${this.sym}${total.toFixed(2)}</span>
            </div>
            <div class="held-card-meta">
              <span>${units} item${units === 1 ? "" : "s"}</span>
              <span class="held-card-dot">·</span>
              <span>${when}</span>
            </div>
          </div>
          <button type="button" class="held-card-delete" title="Discard this held sale"
                  aria-label="Discard held sale"
                  data-action="click->pos#deleteHeld" data-held-id="${s.id}">
            <i class="bi bi-x-lg"></i>
          </button>
        </div>`
      }).join("")
  }

  // Compact relative time ("just now", "5m", "2h", "3d") from a Date.now() id.
  timeAgo(ts) {
    const secs = Math.max(0, Math.floor((Date.now() - ts) / 1000))
    if (secs < 60) return "just now"
    const mins = Math.floor(secs / 60)
    if (mins < 60) return `${mins}m ago`
    const hrs = Math.floor(mins / 60)
    if (hrs < 24) return `${hrs}h ago`
    return `${Math.floor(hrs / 24)}d ago`
  }

  recallHeld(event) {
    const id = parseInt(event.currentTarget.dataset.heldId)
    this.recallSale(id)
  }

  deleteHeld(event) {
    event.stopPropagation()
    const id = parseInt(event.currentTarget.dataset.heldId)
    const held = JSON.parse(localStorage.getItem("pos_held_sales") || "[]")
    const sale = held.find(s => s.id === id)
    const units = sale ? sale.cart.reduce((n, i) => n + i.quantity, 0) : 0
    if (!confirm(`Discard this held sale (${units} item${units === 1 ? "" : "s"})? This can't be undone.`)) return
    localStorage.setItem("pos_held_sales", JSON.stringify(held.filter(s => s.id !== id)))
    this.renderHeldSales()
    this.showToast("Held sale discarded.", "info")
  }

  applyDiscount() {
    const val = parseFloat(this.discountInputTarget.value) || 0
    this.discountPct = Math.min(Math.max(val, 0), 100)
    this.calculateTotals()
  }

  updateItemDiscount(event) {
    const item = this.cartItemByEvent(event)
    if (!item) return
    const val = parseFloat(event.currentTarget.value) || 0
    item.discount_pct = Math.min(Math.max(val, 0), 100)
    this.updateCart()
  }

  cartItemByEvent(event) {
    const productId = parseInt(event.currentTarget.dataset.productId)
    return this.cart.find(i => i.product_id === productId)
  }

  updateCart() {
    const count = this.cart.reduce((s, i) => s + i.quantity, 0)

    this.cartBadgeTarget.textContent = count
    this.cartBadgeTarget.classList.toggle("d-none", count === 0)

    if (this.cart.length === 0) {
      this.cartEmptyTarget.classList.remove("d-none")
      this.cartEmptyTarget.style.display = ""
      this.cartItemsTarget.innerHTML = ""
      this.checkoutBtnTarget.disabled = true
      this._cartIndex = 0
      // Otherwise focus is left on the deleted node.
      if (this._pendingCartFocus != null) {
        this._pendingCartFocus = null
        this.focusSearch()
      }
    } else {
      this.cartEmptyTarget.classList.add("d-none")
      this.cartEmptyTarget.style.display = "none"
      this.checkoutBtnTarget.disabled = false
      this.renderCartItems()
    }

    this.calculateTotals()
    this.fetchPromoPreview()
  }

  fetchPromoPreview() {
    clearTimeout(this._promoTimer)
    if (this.cart.length === 0) {
      this._promoDiscount = 0
      this._promoName = null
      this._promoId = null
      this.calculateTotals()
      return
    }
    this._promoTimer = setTimeout(() => {
      const payload = {
        customer_id: this.selectedCustomerId,
        items: this.cart.map(i => ({ product_id: i.product_id, quantity: i.quantity }))
      }
      fetch(this.previewUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": this.csrfTokenValue },
        body: JSON.stringify(payload)
      })
        .then(r => r.json())
        .then(data => {
          this._promoDiscount = data.promotion_discount || 0
          this._promoName = data.promotion_name || null
          this._promoId = data.promotion_id || null
          this.calculateTotals()
        })
        .catch(() => { })
    }, 300)
  }

  renderCartItems() {
    this.cartItemsTarget.innerHTML = this.cart.map((item, idx) => {
      const disc = item.discount_pct || 0
      const lineTotal = item.price * item.quantity * (1 - disc / 100)
      const origTotal = item.price * item.quantity
      const totalHtml = disc > 0
        ? `<div style="text-align:right;line-height:1.1;">
             <div style="font-size:.62rem;color:#6b7280;text-decoration:line-through;">${this.sym}${origTotal.toFixed(2)}</div>
             <div class="cart-item-total" style="color:#16a34a;">${this.sym}${lineTotal.toFixed(2)}</div>
           </div>`
        : `<div class="cart-item-total">${this.sym}${lineTotal.toFixed(2)}</div>`
      // The line is the focus stop; its controls stay out of the tab order.
      const label = `Line ${idx + 1}: ${item.name}, quantity ${item.quantity}, ${this.sym}${lineTotal.toFixed(2)}`

      return `
      <div class="cart-item"
           role="group"
           tabindex="${idx === 0 ? 0 : -1}"
           aria-label="${this.escapeHtml(label)}"
           data-product-id="${item.product_id}">
        <div class="cart-item-num">${idx + 1}</div>
        <div class="cart-item-info">
          <div class="cart-item-name" title="${this.escapeHtml(item.name)}">${this.escapeHtml(item.name)}</div>
          <div class="d-flex align-items-center gap-1 mt-1">
            <span class="cart-item-price">${this.sym}${item.price.toFixed(2)} ea</span>
            <div style="display:flex;align-items:center;background:#f9fafb;border:1px solid #e5e7eb;border-radius:4px;padding:1px 4px;gap:2px;">
              <i class="bi bi-tag" style="font-size:.55rem;color:#6b7280;"></i>
              <input type="number" min="0" max="100" step="0.1" value="${disc || ""}"
                     style="width:32px;border:0;background:transparent;font-size:.68rem;font-weight:600;color:#374151;padding:0;box-shadow:none;outline:none;"
                     data-action="change->pos#updateItemDiscount"
                     data-product-id="${item.product_id}"
                     tabindex="-1"
                     aria-label="Discount percent for ${this.escapeHtml(item.name)}"
                     placeholder="0">
              <span style="font-size:.62rem;color:#6b7280;">%</span>
            </div>
          </div>
        </div>
        <div class="cart-qty-wrap">
          <button class="qty-btn" data-action="click->pos#decrementQty" data-product-id="${item.product_id}"
                  tabindex="-1" aria-label="Decrease quantity">
            <i class="bi bi-dash"></i>
          </button>
          <input class="qty-input" type="number" min="1" max="${item.stock}"
                 value="${item.quantity}"
                 data-action="change->pos#updateQtyDirect"
                 data-product-id="${item.product_id}"
                 tabindex="-1"
                 aria-label="Quantity for ${this.escapeHtml(item.name)}">
          <button class="qty-btn" data-action="click->pos#incrementQty" data-product-id="${item.product_id}"
                  tabindex="-1" aria-label="Increase quantity">
            <i class="bi bi-plus"></i>
          </button>
        </div>
        ${totalHtml}
        <button class="qty-btn remove-btn ms-1" data-action="click->pos#removeItem" data-product-id="${item.product_id}"
                tabindex="-1" title="Remove" aria-label="Remove ${this.escapeHtml(item.name)}">
          <i class="bi bi-x"></i>
        </button>
      </div>`
    }).join("")

    // Clamped: removing a line leaves the list shorter than the index.
    if (this._pendingCartFocus != null) {
      const i = this._pendingCartFocus
      this._pendingCartFocus = null
      this._focusCartRow(Math.min(i, this.cart.length - 1))
    } else {
      this._cartIndex = Math.min(this._cartIndex, this.cart.length - 1)
    }
  }

  // Mirrors SalesController#create, tax rounded per line as the server does.
  computeTotals() {
    let subtotal = 0
    this.cart.forEach(item => {
      subtotal += item.price * item.quantity * (1 - (item.discount_pct || 0) / 100)
    })

    const discountAmt = subtotal * this.discountPct / 100
    const promoDiscount = this._promoDiscount || 0
    const afterDiscounts = Math.max(0, subtotal - discountAmt - promoDiscount)

    const discountRatio = subtotal > 0 ? afterDiscounts / subtotal : 0
    let taxTotal = 0
    this.cart.forEach(item => {
      const itemEffective = item.price * item.quantity * (1 - (item.discount_pct || 0) / 100)
      const effectiveAfter = itemEffective * discountRatio
      const rate = (item.tax_rate || 0) / 100
      const itemTax = this.taxInclusiveValue
        ? effectiveAfter - effectiveAfter / (1 + rate)
        : effectiveAfter * rate
      taxTotal += Math.round(itemTax * 100) / 100
    })

    let total = this.taxInclusiveValue ? afterDiscounts : afterDiscounts + taxTotal
    // earn_points! works off total_amount before any points are redeemed.
    const earnBase = total

    let pointsDiscount = 0
    let pointsUsed = 0
    if (this.redeemingPoints && this.selectedCustomerData && this.selectedCustomerData.loyalty_points > 0) {
      const maxPointValue = this.selectedCustomerData.points_value
      pointsDiscount = Math.min(maxPointValue, total)
      pointsUsed = Math.ceil(pointsDiscount * 100) // 1 point = $0.01
      total = total - pointsDiscount
    }

    return {
      subtotal,
      discountAmt,
      promoDiscount,
      taxTotal,
      earnBase,
      pointsDiscount,
      pointsUsed,
      total: Math.max(total, 0)
    }
  }

  calculateTotals() {
    const { subtotal, discountAmt, promoDiscount, taxTotal, earnBase,
      pointsDiscount, pointsUsed, total } = this.computeTotals()

    this._pointsDiscount = pointsDiscount
    this._pointsUsed = pointsUsed

    this.subtotalTarget.textContent = `${this.sym}${subtotal.toFixed(2)}`
    this.taxAmountTarget.textContent = `${this.sym}${taxTotal.toFixed(2)}`
    this.totalAmountTarget.textContent = `${this.sym}${total.toFixed(2)}`

    if (this.discountPct > 0) {
      this.discountRowTarget.style.removeProperty("display")
      this.discountAmountTarget.textContent = `-${this.sym}${discountAmt.toFixed(2)}`
    } else {
      this.discountRowTarget.style.display = "none"
    }

    if (this.hasPromoRowTarget) {
      if (promoDiscount > 0 && this._promoName) {
        this.promoRowTarget.style.removeProperty("display")
        this.promoAmountTarget.textContent = `-${this.sym}${promoDiscount.toFixed(2)}`
        this.promoLabelTarget.innerHTML = `<i class="bi bi-tags me-1"></i>${this.escapeHtml(this._promoName)}`
      } else {
        this.promoRowTarget.style.display = "none"
      }
    }

    if (this.hasRedeemRowTarget) {
      if (pointsDiscount > 0) {
        this.redeemRowTarget.style.removeProperty("display")
        this.redeemAmountTarget.textContent = `-${this.sym}${pointsDiscount.toFixed(2)}`
      } else {
        this.redeemRowTarget.style.display = "none"
      }
    }

    if (this.hasPointsEarnedRowTarget) {
      if (this.selectedCustomerData && this.cart.length > 0) {
        const tierMultipliers = { bronze: 1.0, silver: 1.25, gold: 1.5, platinum: 2.0 }
        const mult = tierMultipliers[this.selectedCustomerData.loyalty_tier] || 1.0
        // Mirrors Customer#earn_points!: earn rate, then tier multiplier.
        const basePoints = Math.floor(earnBase * this.pointsPerUnitValue)
        const estimated = Math.floor(basePoints * mult)
        this.pointsEarnedRowTarget.style.removeProperty("display")
        this.pointsEarnedAmountTarget.textContent = `+${estimated} pts`
      } else {
        this.pointsEarnedRowTarget.style.display = "none"
      }
    }
  }

  searchCustomer() {
    const query = this.customerSearchTarget.value.toLowerCase().trim()
    if (query.length < 2) {
      this.customerDropdownTarget.classList.add("d-none")
      this._customerIndex = -1
      this.customerSearchTarget.setAttribute("aria-expanded", "false")
      this._setActiveDescendant(this.customerSearchTarget, null)
      return
    }

    const filtered = this.customersValue.filter(c =>
      c.name.toLowerCase().includes(query) ||
      (c.phone && c.phone.includes(query))
    ).slice(0, 6)

    const addBtn = `<button type="button" class="list-group-item list-group-item-action d-flex align-items-center gap-2"
              style="font-size:.8rem;padding:.45rem .75rem;color:#248317;font-weight:600;"
              role="option" aria-selected="false" data-kb-option
              id="pos-customer-opt-add"
              data-action="click->pos#openNewCustomer">
          <i class="bi bi-person-plus"></i> Add new customer <kbd class="pos-inline-kbd">Alt+N</kbd>
        </button>`

    if (filtered.length === 0) {
      this.customerDropdownTarget.innerHTML = `<div class="list-group-item text-muted small py-2">No customers found</div>` + addBtn
    } else {
      this.customerDropdownTarget.innerHTML = filtered.map((c, n) => `
        <button type="button" class="list-group-item list-group-item-action"
                style="font-size:.82rem;padding:.5rem .75rem;"
                role="option" aria-selected="false" data-kb-option
                id="pos-customer-opt-${n}"
                data-action="click->pos#selectCustomer"
                data-customer-id="${c.id}"
                data-customer-name="${this.escapeHtml(c.name)}">
          <div class="d-flex align-items-center gap-2">
            <div style="width:26px;height:26px;border-radius:50%;background:#f0fdf4;border:1px solid #bbf7d0;display:flex;align-items:center;justify-content:center;font-size:.7rem;font-weight:700;color:#16a34a;flex-shrink:0;">
              ${this.escapeHtml(c.name.charAt(0).toUpperCase())}
            </div>
            <div>
              <div class="fw-semibold" style="color:#111827;">${this.escapeHtml(c.name)}</div>
              ${c.phone ? `<div style="font-size:.72rem;color:#6b7280;">${this.escapeHtml(c.phone)}</div>` : ''}
            </div>
            <span class="ms-auto badge" style="background:#eff6ff;color:#2563eb;font-size:.65rem;">${c.loyalty_points} pts</span>
          </div>
        </button>
      `).join("") + addBtn
    }
    this._customerIndex = -1
    this._setActiveDescendant(this.customerSearchTarget, null)
    this.customerDropdownTarget.classList.remove("d-none")
    this.customerSearchTarget.setAttribute("aria-expanded", "true")
  }

  selectCustomer(event) {
    this.selectedCustomerId = parseInt(event.currentTarget.dataset.customerId)
    this.selectedCustomerData = this.customersValue.find(c => c.id === this.selectedCustomerId)
    const name = event.currentTarget.dataset.customerName
    this.customerNameTarget.textContent = name
    this.customerSelectedTarget.classList.remove("d-none")
    this.customerSearchTarget.value = ""
    this._closeDropdowns()
    this.updateLoyaltyPanel()
    if (this.paymentMethod === "credit") this._updateCreditSection()
    this.fetchPromoPreview()
  }

  clearCustomer() {
    this.selectedCustomerId = null
    this.selectedCustomerData = null
    this.redeemingPoints = false
    this.customerSelectedTarget.classList.add("d-none")
    this.customerSearchTarget.value = ""
    this.updateLoyaltyPanel()
    this.fetchPromoPreview()
  }

  openNewCustomer() {
    this._rememberFocus()
    this.newCustomerNameTarget.value = ""
    this.newCustomerPhoneTarget.value = ""
    this.newCustomerEmailTarget.value = ""
    this.newCustomerErrorTarget.classList.add("d-none")
    this.newCustomerErrorTarget.textContent = ""
    // Shared closer, so aria-expanded and the highlight index reset too.
    this._closeDropdowns()
    this.newCustomerModalTarget.classList.add("show")
    this.newCustomerModalTarget.style.display = "block"
    document.body.classList.add("modal-open")
    setTimeout(() => this.newCustomerNameTarget.focus(), 50)
  }

  closeNewCustomer() {
    this.newCustomerModalTarget.classList.remove("show")
    this.newCustomerModalTarget.style.display = "none"
    document.body.classList.remove("modal-open")
    this._restoreFocus(() => this.focusCustomer())
  }

  async saveNewCustomer() {
    const name = this.newCustomerNameTarget.value.trim()
    if (!name) {
      this.newCustomerErrorTarget.textContent = "Name is required."
      this.newCustomerErrorTarget.classList.remove("d-none")
      return
    }

    const phone = this.newCustomerPhoneTarget.value.trim()
    if (!phone) {
      this.newCustomerErrorTarget.textContent = "Phone is required."
      this.newCustomerErrorTarget.classList.remove("d-none")
      return
    }

    this.newCustomerSaveBtnTarget.disabled = true
    this.newCustomerSaveBtnTarget.innerHTML = '<span class="spinner-border spinner-border-sm me-1"></span> Saving…'

    try {
      const res = await fetch(this.quickCreateCustomerUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": this.csrfTokenValue },
        body: JSON.stringify({ customer: { name, phone: this.newCustomerPhoneTarget.value.trim(), email: this.newCustomerEmailTarget.value.trim() } })
      })
      const data = await res.json()

      if (!res.ok) {
        this.newCustomerErrorTarget.textContent = (data.errors || ["Something went wrong."]).join(", ")
        this.newCustomerErrorTarget.classList.remove("d-none")
        return
      }

      this.customersValue = [...this.customersValue, data]
      this.closeNewCustomer()
      this.customerNameTarget.textContent = data.name
      this.selectedCustomerId = data.id
      this.selectedCustomerData = data
      this.customerSelectedTarget.classList.remove("d-none")
      this.customerSearchTarget.value = ""
      this.updateLoyaltyPanel()
      this.fetchPromoPreview()
    } finally {
      this.newCustomerSaveBtnTarget.disabled = false
      this.newCustomerSaveBtnTarget.innerHTML = '<i class="bi bi-check-circle me-1"></i> Save Customer'
    }
  }

  updateLoyaltyPanel() {
    if (!this.hasLoyaltyPanelTarget) return
    const c = this.selectedCustomerData
    if (!c || c.loyalty_points <= 0) {
      this.loyaltyPanelTarget.classList.add("d-none")
      return
    }

    const tierColors = { bronze: "#cd7f32", silver: "#6b7280", gold: "#eab308", platinum: "#8b5cf6" }
    const color = tierColors[c.loyalty_tier] || "#6b7280"

    this.loyaltyInfoTarget.innerHTML = `
      <div class="d-flex align-items-center gap-2">
        <i class="bi bi-award-fill" style="color:${color};font-size:.85rem;"></i>
        <span style="font-size:.72rem;font-weight:700;color:#374151;">${c.loyalty_points} pts</span>
        <span style="font-size:.62rem;background:${color}22;color:${color};padding:1px 7px;border-radius:99px;font-weight:600;text-transform:uppercase;">${c.loyalty_tier}</span>
        <span style="font-size:.65rem;color:#6b7280;margin-left:auto;">= ${this.sym}${c.points_value.toFixed(2)}</span>
      </div>`

    this.loyaltyPanelTarget.classList.remove("d-none")
  }

  toggleRedeemPoints() {
    if (!this.selectedCustomerData || this.selectedCustomerData.loyalty_points <= 0) return
    this.redeemingPoints = !this.redeemingPoints
    if (this.hasRedeemToggleTarget) {
      this.redeemToggleTarget.checked = this.redeemingPoints
    }
    this.calculateTotals()
  }

  openPayment() {
    if (this.cart.length === 0) return
    this._rememberFocus()

    const total = this.getTotal()
    const itemCount = this.cart.reduce((s, i) => s + i.quantity, 0)

    this.modalTotalTarget.textContent = `${this.sym}${total.toFixed(2)}`
    if (this.hasModalItemCountTarget) {
      this.modalItemCountTarget.textContent = `${itemCount} item${itemCount !== 1 ? 's' : ''} · ${this.cart.length} product${this.cart.length !== 1 ? 's' : ''}`
    }

    this.paymentMethod = "cash"
    this.updatePaymentMethodUI()

    if (this.hasCashInputTarget) {
      this.cashInputTarget.value = ""
      this.changeAmountTarget.textContent = `${this.sym}0.00`
      this.changeAmountTarget.style.color = ""
    }

    if (this.hasSplitAmount1Target) this.splitAmount1Target.value = ""
    if (this.hasSplitAmount2Target) this.splitAmount2Target.value = ""
    if (this.hasSplitRemainingTarget) this.splitRemainingTarget.textContent = `${this.sym}0.00`
    if (this.hasSplitBarTarget) this.splitBarTarget.style.width = "0%"
    if (this.hasSplitMessageTarget) this.splitMessageTarget.textContent = ""

    this.renderQuickCash(total)
    this.payBtnTarget.disabled = true
    this.paymentModalTarget.classList.add("show")
    this.paymentModalTarget.style.display = "block"
    document.body.classList.add("modal-open")

    this.backdrop = document.createElement("div")
    this.backdrop.className = "modal-backdrop fade show"
    document.body.appendChild(this.backdrop)

    setTimeout(() => {
      if (this.hasCashInputTarget && this.paymentMethod === "cash") this.cashInputTarget.focus()
    }, 200)
  }

  closePayment() {
    // With the sheet shut, restoring focus would yank it out of live work.
    if (!this._isOpen(this.paymentModalTarget)) return

    this.paymentModalTarget.classList.remove("show")
    this.paymentModalTarget.style.display = "none"
    document.body.classList.remove("modal-open")
    if (this.backdrop) { this.backdrop.remove(); this.backdrop = null }
    this._restoreFocus(() => this.checkoutBtnTarget.focus())
  }

  renderQuickCash(total) {
    if (!this.hasQuickCashTarget) return

    const denominations = this.denominationsValue
    const roundedUp = denominations.filter(d => d >= total)

    let buttons = `
      <button class="denom-btn exact" data-action="click->pos#setQuickCash" data-amount="${total.toFixed(2)}">
        Exact<br><span style="font-size:.7rem;">${this.sym}${total.toFixed(2)}</span>
      </button>`

    const shown = new Set()
    roundedUp.slice(0, 4).forEach(d => {
      if (!shown.has(d)) {
        shown.add(d)
        buttons += `<button class="denom-btn" data-action="click->pos#setQuickCash" data-amount="${d}">${this.sym}${d}</button>`
      }
    })

    if (shown.size < 3) {
      denominations.forEach(d => {
        if (!shown.has(d) && shown.size < 4) {
          shown.add(d)
          buttons += `<button class="denom-btn" data-action="click->pos#setQuickCash" data-amount="${d}">${this.sym}${d}</button>`
        }
      })
    }

    this.quickCashTarget.innerHTML = buttons
  }

  setQuickCash(event) {
    const amount = parseFloat(event.currentTarget.dataset.amount)
    this.cashInputTarget.value = amount.toFixed(2)
    this.validateCashInput()
    this.cashInputTarget.focus()
  }

  selectPaymentMethod(event) {
    this.paymentMethod = event.currentTarget.dataset.method
    this.updatePaymentMethodUI()
  }

  updatePaymentMethodUI() {
    this.paymentMethodBtnsTargets.forEach(btn => {
      const active = btn.dataset.method === this.paymentMethod
      btn.classList.toggle("active", active)
    })

    this.cashSectionTarget.classList.toggle("d-none", this.paymentMethod !== "cash")
    this.cardSectionTarget.classList.toggle("d-none", this.paymentMethod !== "card")
    if (this.hasMoneySectionTarget) this.moneySectionTarget.classList.toggle("d-none", this.paymentMethod !== "mobile_money")
    if (this.hasCreditSectionTarget) this.creditSectionTarget.classList.toggle("d-none", this.paymentMethod !== "credit")
    if (this.hasSplitSectionTarget) this.splitSectionTarget.classList.toggle("d-none", this.paymentMethod !== "split")

    if (this.paymentMethod === "card" || this.paymentMethod === "mobile_money") {
      this.payBtnTarget.disabled = false
    } else if (this.paymentMethod === "credit") {
      this._updateCreditSection()
    } else if (this.paymentMethod === "split") {
      this.validateSplitInput()
    } else {
      this.validateCashInput()
    }
  }

  _updateCreditSection() {
    if (!this.hasCreditInfoTarget) return
    const c = this.selectedCustomerData
    if (!c) {
      this.creditInfoTarget.innerHTML = `<span style="color:#dc2626;">Please select a customer to use store credit.</span>`
      this.payBtnTarget.disabled = true
      return
    }
    const available = (c.credit_limit - c.credit_balance).toFixed(2)
    const total = this.getTotal()
    const canPay = parseFloat(available) >= total
    this.creditInfoTarget.innerHTML = `
      <div style="display:flex;justify-content:space-between;margin-bottom:3px;">
        <span>Credit Limit</span><span>${this.sym}${c.credit_limit.toFixed(2)}</span>
      </div>
      <div style="display:flex;justify-content:space-between;margin-bottom:3px;">
        <span>Balance Owed</span><span>${this.sym}${c.credit_balance.toFixed(2)}</span>
      </div>
      <div style="display:flex;justify-content:space-between;font-weight:700;border-top:1px dashed #fde68a;padding-top:3px;">
        <span>Available</span><span style="color:${canPay ? '#15803d' : '#dc2626'};">${this.sym}${parseFloat(available).toFixed(2)}</span>
      </div>
      ${!canPay ? `<div style="color:#dc2626;margin-top:4px;font-size:.75em;">Insufficient credit for this sale (${this.sym}${total.toFixed(2)} needed).</div>` : ""}`
    this.payBtnTarget.disabled = !canPay
  }

  calculateChange() { this.validateCashInput() }

  validateSplitInput() {
    if (!this.hasSplitAmount1Target || !this.hasSplitAmount2Target) return
    const total = Math.round(this.getTotal() * 100) / 100
    const a1 = Math.round((parseFloat(this.splitAmount1Target.value) || 0) * 100) / 100
    const a2 = Math.round((parseFloat(this.splitAmount2Target.value) || 0) * 100) / 100
    const paid = Math.round((a1 + a2) * 100) / 100
    const remaining = Math.round((total - paid) * 100) / 100

    if (this.hasSplitBarTarget) {
      const pct = total > 0 ? Math.min((paid / total) * 100, 100) : 0
      this.splitBarTarget.style.width = `${pct}%`
      this.splitBarTarget.style.background = pct >= 100
        ? "linear-gradient(90deg,#248317,#4ade80)"
        : "linear-gradient(90deg,#f59e0b,#fbbf24)"
    }

    if (this.hasSplitRemainingTarget) {
      if (remaining <= 0) {
        this.splitRemainingTarget.textContent = `${this.sym}0.00`
        this.splitRemainingTarget.style.color = "#248317"
      } else {
        this.splitRemainingTarget.textContent = `${this.sym}${remaining.toFixed(2)}`
        this.splitRemainingTarget.style.color = "#dc2626"
      }
    }

    const m1 = this.hasSplitMethod1Target ? this.splitMethod1Target.value : "cash"
    const m2 = this.hasSplitMethod2Target ? this.splitMethod2Target.value : "card"
    const sameMethod = m1 === m2

    if (this.hasSplitMessageTarget) {
      if (sameMethod) {
        this.splitMessageTarget.textContent = "Select two different payment methods."
        this.splitMessageTarget.style.color = "#dc2626"
      } else if (remaining > 0 && (a1 > 0 || a2 > 0)) {
        this.splitMessageTarget.textContent = `${this.sym}${remaining.toFixed(2)} still unpaid`
        this.splitMessageTarget.style.color = "#f59e0b"
      } else if (remaining <= 0 && a1 > 0 && a2 > 0) {
        this.splitMessageTarget.textContent = "Ready to complete"
        this.splitMessageTarget.style.color = "#248317"
      } else {
        this.splitMessageTarget.textContent = ""
      }
    }

    this.payBtnTarget.disabled = !(a1 > 0 && a2 > 0 && remaining <= 0 && !sameMethod)
  }

  splitFillRemainder(event) {
    if (!this.hasSplitAmount1Target || !this.hasSplitAmount2Target) return
    const target = event.currentTarget.dataset.splitTarget
    const total = Math.round(this.getTotal() * 100) / 100
    if (target === "1") {
      const a2 = Math.round((parseFloat(this.splitAmount2Target.value) || 0) * 100) / 100
      const fill = Math.max(total - a2, 0)
      this.splitAmount1Target.value = fill.toFixed(2)
    } else {
      const a1 = Math.round((parseFloat(this.splitAmount1Target.value) || 0) * 100) / 100
      const fill = Math.max(total - a1, 0)
      this.splitAmount2Target.value = fill.toFixed(2)
    }
    this.validateSplitInput()
  }

  validateCashInput() {
    const total = Math.round(this.getTotal() * 100) / 100
    const cashGiven = parseFloat(this.cashInputTarget.value) || 0
    const change = Math.round((cashGiven - total) * 100) / 100

    if (cashGiven > 0 && change >= 0) {
      this.changeAmountTarget.textContent = `${this.sym}${change.toFixed(2)}`
      this.changeAmountTarget.style.color = "#16a34a"
      this.payBtnTarget.disabled = false
    } else if (cashGiven > 0) {
      this.changeAmountTarget.textContent = `-${this.sym}${Math.abs(change).toFixed(2)}`
      this.changeAmountTarget.style.color = "#dc2626"
      this.payBtnTarget.disabled = true
    } else {
      this.changeAmountTarget.textContent = `${this.sym}0.00`
      this.changeAmountTarget.style.color = ""
      this.payBtnTarget.disabled = true
    }
  }

  async processPayment() {
    if (this.cart.length === 0) return

    this.payBtnTarget.disabled = true
    this.payBtnTarget.innerHTML = `<span class="spinner-border spinner-border-sm me-2"></span>Processing…`

    const total = this.getTotal()
    let payments
    if (this.paymentMethod === "split") {
      const m1 = this.hasSplitMethod1Target ? this.splitMethod1Target.value : "cash"
      const m2 = this.hasSplitMethod2Target ? this.splitMethod2Target.value : "card"
      const a1 = parseFloat(this.splitAmount1Target.value) || 0
      const a2 = parseFloat(this.splitAmount2Target.value) || 0
      payments = []
      if (a1 > 0) payments.push({ method: m1, amount: a1 })
      if (a2 > 0) payments.push({ method: m2, amount: a2 })
    } else if (this.paymentMethod === "cash") {
      // `amount` settles the sale; sending cash received books change as takings.
      const cashGiven = parseFloat(this.cashInputTarget.value) || 0
      payments = [{
        method: "cash",
        amount: Math.min(cashGiven, total),
        tendered: cashGiven
      }]
    } else {
      payments = [{ method: this.paymentMethod, amount: total }]
    }

    const payload = {
      customer_id: this.selectedCustomerId,
      discount_percent: this.discountPct,
      redeem_points: this._pointsUsed || 0,
      promotion_id: this._promoId || null,
      items: this.cart.map(i => ({ product_id: i.product_id, quantity: i.quantity, discount_pct: i.discount_pct || 0 })),
      payments: payments
    }

    try {
      const response = await fetch(this.createUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": this.csrfTokenValue },
        body: JSON.stringify(payload)
      })

      const data = await response.json()

      if (response.ok && data.success) {
        this.closePayment()
        this._lastSaleData = data
        if (data.receipt_url) this.loadReceiptFrame(data.receipt_url)
        this._resetAfterSale()
      } else {
        this.showToast(data.error || "Failed to process sale", "danger")
        this.payBtnTarget.disabled = false
        this.payBtnTarget.innerHTML = `<i class="bi bi-check-circle me-1"></i> Complete Sale`
      }
    } catch {
      this.showToast("Network error — please try again", "danger")
      this.payBtnTarget.disabled = false
      this.payBtnTarget.innerHTML = `<i class="bi bi-check-circle me-1"></i> Complete Sale`
    }
  }

  // Payment sheet keys

  _paymentKeys(e) {
    this._trapFocus(this.paymentModalTarget, e)

    if (e.key === "Escape") { e.preventDefault(); this.closePayment(); return }

    if (e.altKey && !e.ctrlKey && !e.metaKey) {
      const methods = {
        Digit1: "cash", Digit2: "card", Digit3: "mobile_money",
        Digit4: "credit", Digit5: "split"
      }
      const method = methods[e.code]
      if (method) {
        e.preventDefault()
        this.paymentMethod = method
        this.updatePaymentMethodUI()
        this._focusPaymentSection()
        return
      }
      if (e.code === "KeyE") { e.preventDefault(); this._tenderExact(); return }
    }

    if (e.key === "Enter") {
      // A focused button or select answers Enter itself.
      const tag = (e.target.tagName || "").toLowerCase()
      if (tag === "button" || tag === "select" || tag === "a") return
      e.preventDefault()
      if (!this.payBtnTarget.disabled) this.processPayment()
    }
  }

  _focusPaymentSection() {
    // Deferred a tick: the sections are still d-none this frame.
    setTimeout(() => {
      if (this.paymentMethod === "cash" && this.hasCashInputTarget) {
        this.cashInputTarget.focus(); this.cashInputTarget.select()
      } else if (this.paymentMethod === "split" && this.hasSplitMethod1Target) {
        this.splitMethod1Target.focus()
      } else if (this.hasPayBtnTarget && !this.payBtnTarget.disabled) {
        this.payBtnTarget.focus()
      } else {
        // Complete may be disabled and the old section hidden.
        const btn = this.paymentMethodBtnsTargets.find(b => b.dataset.method === this.paymentMethod)
        if (btn) btn.focus()
      }
    }, 0)
  }

  _tenderExact() {
    if (this.paymentMethod !== "cash" || !this.hasCashInputTarget) return
    this.cashInputTarget.value = this.getTotal().toFixed(2)
    this.validateCashInput()
    this.cashInputTarget.focus()
    this.cashInputTarget.select()
  }

  // New-customer sheet keys

  _newCustomerKeys(e) {
    this._trapFocus(this.newCustomerModalTarget, e)

    if (e.key === "Escape") { e.preventDefault(); this.closeNewCustomer(); return }

    if (e.key === "Enter" && this._isTextEntry(e.target)) {
      // Three fields, one action: Enter from any of them saves.
      e.preventDefault()
      this.saveNewCustomer()
    }
  }

  // Panel resize by keyboard

  _setLeftWidth(px) {
    const sidebarW = this.element.querySelector('.pos-action-sidebar')?.offsetWidth || 0
    const max = window.innerWidth - sidebarW - 306
    const w = Math.max(200, Math.min(px, max))
    this._leftColEl.style.flex = 'none'
    this._leftColEl.style.width = w + 'px'
    if (this.hasResizeHandleTarget) {
      this.resizeHandleTarget.setAttribute("aria-valuenow", String(Math.round(w)))
      this.resizeHandleTarget.setAttribute("aria-valuemin", "200")
      this.resizeHandleTarget.setAttribute("aria-valuemax", String(Math.round(max)))
    }
    return w
  }

  resizeKeys(event) {
    const step = event.shiftKey ? 60 : 20
    switch (event.key) {
      case "ArrowRight": event.preventDefault(); this._setLeftWidth(this._leftColEl.offsetWidth + step); return
      case "ArrowLeft": event.preventDefault(); this._setLeftWidth(this._leftColEl.offsetWidth - step); return
      case "Home": event.preventDefault(); this._setLeftWidth(200); return
      case "End": event.preventDefault(); this._setLeftWidth(window.innerWidth); return
      case "Enter":
      case " ":
        event.preventDefault()
        this.toggleLeftPanel()
        return
    }
  }

  // Receipt printing

  // Manual print and reprint, bound to F8.
  printReceipt() {
    if (!this.hasReceiptFrameTarget || !this.receiptFrameTarget.contentWindow) return
    if (!this.receiptFrameTarget.contentDocument?.querySelector(".receipt")) {
      this.showToast("No receipt to print yet", "info")
      return
    }

    this.receiptFrameTarget.contentWindow.print()

    this._printCount = (this._printCount || 0) + 1
    this.element.dataset.receiptPrints = String(this._printCount)
  }

  // Opt a till out with localStorage.setItem("pos_auto_print", "off").
  get autoPrintEnabled() {
    if (localStorage.getItem("pos_auto_print") === "off") return false
    return this.autoPrintValue
  }

  _autoPrintReceipt() {
    if (!this.autoPrintEnabled || this._autoPrinted) return

    // A failed request fires onload too, with an error page in the frame.
    if (!this.receiptFrameTarget.contentDocument?.querySelector(".receipt")) {
      this.showToast("Receipt did not load — reprint it from Sales History", "warning")
      return
    }

    this._autoPrinted = true

    // Let the toast paint before any print dialog covers it.
    requestAnimationFrame(() => requestAnimationFrame(() => this.printReceipt()))
  }

  _resetAfterSale() {
    const data = this._lastSaleData
    if (this.hasPayBtnTarget) {
      this.payBtnTarget.disabled = false
      this.payBtnTarget.innerHTML = `<i class="bi bi-check-circle me-1"></i> Complete Sale`
    }
    this.cart = []
    this.discountPct = 0
    if (this.hasDiscountInputTarget) this.discountInputTarget.value = ""
    this.selectedCustomerId = null
    this.clearCustomer()
    this.updateCart()
    this.renderProducts()
    this.focusBarcode()
    if (data) this.showToast(this._saleToastMessage(data), "success")
  }

  // Change leads: no panel states it any more.
  _saleToastMessage(data) {
    const change = Number(data.change_due || 0)
    if (change > 0) {
      return `Change ${this.sym}${change.toFixed(2)} · ${data.invoice_number}`
    }
    return `Sale ${data.invoice_number} completed!`
  }

  loadReceiptFrame(url) {
    if (!this.hasReceiptFrameTarget) return
    const frame = this.receiptFrameTarget
    this._autoPrinted = false
    frame.onload = () => this._autoPrintReceipt()
    frame.src = url
  }

  getTotal() {
    return this.computeTotals().total
  }

  escapeHtml(text) {
    const d = document.createElement("div")
    d.textContent = text ?? ""
    // innerHTML leaves quotes alone, and 6" Pipe would break out of an attr.
    return d.innerHTML.replace(/"/g, "&quot;")
  }

  showToast(message, type = "info") {
    const container = document.getElementById("pos-toast-container")
    if (!container) return

    const iconMap = { success: "bi-check-circle-fill", danger: "bi-x-circle-fill", warning: "bi-exclamation-triangle-fill", info: "bi-info-circle-fill" }
    const colorMap = { success: "#16a34a", danger: "#dc2626", warning: "#d97706", info: "#2563eb" }

    const toast = document.createElement("div")
    toast.className = "toast align-items-center border-0 show"
    toast.setAttribute("role", "alert")
    toast.style.cssText = `background:#1a2332;color:#fff;border-radius:10px;min-width:200px;box-shadow:0 8px 24px rgba(0,0,0,.25);`
    toast.innerHTML = `
      <div class="d-flex align-items-center gap-2 p-3">
        <i class="bi ${iconMap[type] || 'bi-info-circle-fill'}" style="font-size:1rem;color:${colorMap[type] || '#6b7280'};flex-shrink:0;"></i>
        <span style="font-size:.83rem;font-weight:500;">${this.escapeHtml(message)}</span>
        <button type="button" class="btn-close btn-close-white ms-auto" style="font-size:.65rem;" data-bs-dismiss="toast"></button>
      </div>`
    container.appendChild(toast)

    const bsToast = new Toast(toast, { delay: 2500 })
    bsToast.show()
    toast.addEventListener("hidden.bs.toast", () => toast.remove())
  }
}
